import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { BookOpen } from 'lucide-react';

const ProloguePage = () => {
  return (
    <article className="prose prose-lg max-w-none">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">Prologue</h1>
        <p className="text-xl text-gray-600">The Simple Clarity of Scripture</p>
      </div>

      {/* Letter Section */}
      <div className="mb-16">
        <h2 className="text-3xl font-bold text-center mb-8">A Letter to You, Before We Begin</h2>
        <div className="bg-amber-50 border-l-4 border-amber-600 p-8 rounded-r-lg">
          <p className="text-gray-800 leading-relaxed mb-4">
            <strong>Dear Reader,</strong>
          </p>
          <p className="text-gray-800 leading-relaxed mb-4">
            I want to tell you about the moment I saw it.
          </p>
          <p className="text-gray-800 leading-relaxed mb-4">
            I was raised in a theological printing house. A Scriptorium. Not just reading and studying the Bible for 50 years but also printing and checking every doctrine in Christianity. I had sat under teachers, studied thick commentaries, listened to sermons, been told many theories about when Jesus would come back — that the Church would be quietly lifted out before the worst days of history, safe in heaven while the world below suffered.
          </p>
          <p className="text-gray-800 leading-relaxed mb-4">
            And then one day — Valentine's Day, 1997 — the Lord visited me. Drew me up in visions. When I returned, all that I had learned dissipated. Much of what I was taught in the flesh did not match what I learned in the Spirit. Over the years I struggled, sitting with the text alone. Learning not to rely on commentaries and systematic theology books to tell me what man thinks the Bible says.
          </p>
          <p className="text-gray-800 leading-relaxed mb-4">
            Then the Lord showed me a simple tool. The Grammar Integrity Tests. G.I.T. We all learned it in grade school. How to locate the subject, verb, and object of the sentence. The grammar of the text is preserved so plainly — and it reveals the many erroneous opinions of men.
          </p>
          <p className="text-gray-800 leading-relaxed mb-4">
            And I sat there wondering: how did I miss that? How did so many of us miss that? It is right there. It has always been right there. No Greek degree required. No commentary needed. Just — reading what the Word of God says — simple grammar tools implemented with conscious intent.
          </p>
          <p className="text-gray-800 leading-relaxed mb-4">
            That moment is what this little book is for. God gave you the tools to understand his word that you have no need of any man teach you. His spirit and the rules of grammar.
          </p>
          <p className="text-gray-800 leading-relaxed mb-4">
            This book is for your grandmother who never went to seminary. It is for the new believer sitting in the back row. It is for the teenager reading over someone's shoulder, wondering why the adults argue so much about something that seems, when you just read it, so plain.
          </p>
          <p className="text-gray-800 leading-relaxed mb-4">
            What you are holding is a puzzle guide. We are going to find four corner pieces of one of the greatest questions in all of Scripture: <em>When does Jesus come back for His people?</em>
          </p>
          <p className="text-gray-800 leading-relaxed mb-4">
            Once the corners are in place, the picture builds itself. You will not need me to tell you what it shows. You will see it with your own eyes.
          </p>
          <p className="text-gray-800 leading-relaxed">
            Come along. Let's build this thing together.
          </p>
          <p className="text-gray-800 leading-relaxed mt-6">
            <em>Your friend,<br />Mordecai.</em>
          </p>
        </div>
      </div>

      {/* Treasure Map Section */}
      <div className="mb-12">
        <h2 className="text-2xl font-bold mb-6">From the Gift to the Gathering</h2>
        <p className="text-gray-800 leading-relaxed mb-4">
          Imagine you have a treasure map.
        </p>
        <p className="text-gray-800 leading-relaxed mb-4">
          Not a pretend one. A real one, drawn by someone who buried the treasure himself — who knows every landmark, every path, every fork in the road. He drew it carefully, with love, so that anyone who followed it faithfully would find exactly what he buried.
        </p>
        <p className="text-gray-800 leading-relaxed mb-4">
          Now imagine someone else comes along and says, "I think the map actually means something different. I think when it says north, it really means south..."
        </p>
        <p className="text-gray-800 leading-relaxed mb-4">
          At what point do you stop listening to the explainer — and just follow the map's own internal legend?
        </p>
        <p className="text-gray-800 leading-relaxed mb-4">
          This is something every good map includes. Down in the corner, there is a small box. It tells you what every symbol means. It is called the <strong>legend. The key.</strong>
        </p>
        <p className="text-gray-800 leading-relaxed mb-4">
          Without it, you are guessing. With it, you are reading.
        </p>
        <p className="text-gray-800 leading-relaxed mb-4">
          The Bible is our treasure map. God wrote it. He buried the treasure. And He made the directions plain enough that anyone who follows it faithfully can find what He placed there.
        </p>
      </div>

      {/* Four Questions */}
      <div className="mb-12">
        <h2 className="text-2xl font-bold mb-6">The Scripture's Integrity Check</h2>
        <p className="text-gray-800 leading-relaxed mb-6">
          To follow this map faithfully, we use four simple questions. Every time we read a passage about Christ's return, we ask:
        </p>
        
        <div className="space-y-4">
          <div className="bg-blue-50 border-l-4 border-blue-600 p-6">
            <h3 className="font-bold text-blue-900 mb-2">Question One — The Subject Check</h3>
            <p className="text-blue-900">Who is talking? Who is being talked to? Who is the text about?</p>
          </div>
          
          <div className="bg-green-50 border-l-4 border-green-600 p-6">
            <h3 className="font-bold text-green-900 mb-2">Question Two — The Action Check</h3>
            <p className="text-green-900">What does the text say actually happens? In what order?</p>
          </div>
          
          <div className="bg-purple-50 border-l-4 border-purple-600 p-6">
            <h3 className="font-bold text-purple-900 mb-2">Question Three — The Timing Check</h3>
            <p className="text-purple-900">When does the Bible say this takes place?</p>
          </div>
          
          <div className="bg-red-50 border-l-4 border-red-600 p-6">
            <h3 className="font-bold text-red-900 mb-2">Question Four — The Exclusion Check</h3>
            <p className="text-red-900">What does the plain wording make impossible?</p>
          </div>
        </div>
        
        <p className="text-gray-800 leading-relaxed mt-6">
          Four questions. That is all. You don't need a degree. You don't need Greek. You need a Bible, an honest heart, and the willingness to follow the map wherever it leads.
        </p>
      </div>

      {/* GIT Protocol Table */}
      <div className="mb-12">
        <h2 className="text-2xl font-bold mb-6">What the Grammar Integrity Test Is</h2>
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-300 rounded-lg p-6">
          <p className="text-gray-800 mb-6">
            The Grammar Integrity Test asks basic questions — the same three questions a careful reader of any text should ask:
          </p>
          
          <div className="space-y-4 mb-6">
            <div className="flex items-start gap-4">
              <div className="text-3xl">👤</div>
              <div>
                <strong>WHO</strong> is the text talking about?
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="text-3xl">🔤</div>
              <div>
                <strong>WHAT</strong> does the text say happens?
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="text-3xl">📅</div>
              <div>
                <strong>WHEN</strong> does the text say it happens?
              </div>
            </div>
          </div>
          
          <p className="text-gray-800 text-sm">
            This is the classic 5 W's + H sequence: <strong>Who · What · Where · When · Why · How</strong>
          </p>
        </div>
      </div>

      {/* CTA to Chapter 1 */}
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-300 rounded-lg p-8 text-center mt-12">
        <h3 className="text-2xl font-bold text-gray-900 mb-4">
          Ready to See the Four Corner Pieces?
        </h3>
        <p className="text-gray-700 mb-6">
          Now that you have the legend — the four questions — let's use them to find the four corner pieces of the puzzle.
        </p>
        <Link to="/ebook/chapter-1">
          <Button size="lg" className="bg-amber-600 hover:bg-amber-700">
            <BookOpen className="h-5 w-5 mr-2" />
            Start Chapter 1
          </Button>
        </Link>
        <p className="text-sm text-gray-600 mt-4">
          Free account required to read chapters
        </p>
      </div>
    </article>
  );
};

export default ProloguePage;